import { COL_SCHEDULE_SLOTS } from '../config/collections'
import { pb, PB_NO_CANCEL } from '../pocketbase'
import { formatMachineLabel } from './machines'
import { loadProductMachines } from './productMachines'
import { formatDurationHours, hoursForProductQuantity, productOutputUnitsMatch } from './scheduleCalculations'
import type { MachineRecord, ProductRecord, ScheduleSlotRecord } from '../types/records'

export type PlannedProductionEstimate = {
  date: string
  machineLabel: string
  hint: string
}

function parseNotBeforeMs(notBefore?: string): number {
  if (!notBefore?.trim()) return Date.now()
  const raw = notBefore.trim()
  const d = new Date(raw.includes('T') || raw.includes(' ') ? raw : `${raw.slice(0, 10)}T00:00:00`)
  const ms = d.getTime()
  return Number.isFinite(ms) ? ms : Date.now()
}

/** Оценка даты «Производство (план)» по очереди на совместимых станках. */
export async function estimatePlannedProductionDate(
  product: Pick<ProductRecord, 'id' | 'outputUnit' | 'sheetsPerUnit'>,
  quantity: number,
  options?: {
    notBefore?: string
    excludeLineId?: string
  },
): Promise<PlannedProductionEstimate | null> {
  const qty = Number(quantity) || 0
  if (qty <= 0 || !product.id) return null

  const links = await loadProductMachines(product.id)
  const candidates = links
    .map((link) => ({ link, machine: link.expand?.machine }))
    .filter((row): row is { link: (typeof links)[number]; machine: MachineRecord } => Boolean(row.machine))
    .filter(({ machine }) => productOutputUnitsMatch(product, machine))

  if (candidates.length === 0) return null

  const machineIds = candidates.map(({ machine }) => machine.id)
  const slots = await pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
    filter: `(${machineIds.map((id) => `machine="${id}"`).join(' || ')}) && status != "cancelled"`,
    sort: 'plannedEnd',
    ...PB_NO_CANCEL,
  })

  const baseStartMs = Math.max(Date.now(), parseNotBeforeMs(options?.notBefore))

  let best: { endMs: number; machine: MachineRecord; hours: number; isPrimary: boolean } | null = null

  for (const { link, machine } of candidates) {
    const machineSlots = slots.filter((slot) => {
      if (slot.machine !== machine.id) return false
      if (options?.excludeLineId && slot.requestLine === options.excludeLineId) return false
      return true
    })

    let queueEndMs = baseStartMs
    for (const slot of machineSlots) {
      const endMs = new Date(slot.plannedEnd).getTime()
      if (Number.isFinite(endMs) && endMs > queueEndMs) queueEndMs = endMs
    }

    const hours = hoursForProductQuantity(machine, qty, product)
    if (hours <= 0) continue

    const endMs = queueEndMs + hours * 3600000
    const isPrimary = Boolean(link.isPrimary)
    if (
      !best ||
      endMs < best.endMs ||
      (endMs === best.endMs && isPrimary && !best.isPrimary)
    ) {
      best = { endMs, machine, hours, isPrimary }
    }
  }

  if (!best) return null

  const machineLabel = formatMachineLabel(best.machine)
  return {
    date: new Date(best.endMs).toISOString().slice(0, 10),
    machineLabel,
    hint: `Рассчитано по загрузке ${machineLabel} (~${formatDurationHours(best.hours)} производства)`,
  }
}
